/* eslint-disable react/require-default-props */
import React, { useEffect } from 'react';
import useFetchPropertyChannelSettings from 'components/domain/channelSettings/useFetchPropertyChannelSettings';
import SelectField from 'components/forms/selectField/SelectField';
import { SelectFieldProps } from 'components/forms/selectField/SelectField.types';
import Tooltip from 'components/tooltip/Tooltip';
import { Channel } from 'models/Channels';
import { Property } from 'models/Properties';
import { FeeConditionEnum, FeeScopeEnum } from 'models/PropertyFees';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { classNames } from 'utils/classNameUtils';
import { normalizeKey } from 'utils/localeUtils';
import { MappingTypeContainer } from './PropertyFeeModal.styles';
import { BY_LENGTH_OF_STAY_SCOPE_OPTION } from './PropertyFeeModal.constants';
import useFormData from './useFormData';

interface MappingTypeFieldProps {
  includeEmptySelectOption?: boolean;
  name: string;
  label: string;
  channel: Channel;
  options: string[];
  icon: React.ReactElement;
  labelInputSizes: {
    colSmLabel: number;
    colSmInput: number;
  };
  onChangeHandler?: SelectFieldProps['onChangeHandler'];
  propertyUid: Property['uid'];
}

const MappingTypeField: React.FC<MappingTypeFieldProps> = ({
  includeEmptySelectOption = true,
  name,
  label,
  options,
  channel,
  icon,
  labelInputSizes,
  onChangeHandler,
  propertyUid,
}) => {
  const { t } = useTranslation();
  const {
    isTypeTax,
    scopeValue,
    taxConditionType,
    taxConditionLengthOfStayType,
    isRollInFeeIntoRent,
  } = useFormData();
  const { setValue } = useFormContext();
  const { channelSettings, isFetching } =
    useFetchPropertyChannelSettings(propertyUid);

  const isAirbnbMappingType = channel === Channel.airbnb;
  const isVrboMappingType = channel === Channel.homeaway;
  const isHvmbMappingType = channel === Channel.hvmi;
  const isBookingDotComMappingType = channel === Channel.booking_com;
  const isChannelActiveAtProperty = channelSettings?.some(
    ({ enumId, isActive }) => enumId === channel && isActive,
  );

  const isPetRelatedScope =
    scopeValue === FeeScopeEnum.PER_PET ||
    scopeValue === FeeScopeEnum.PER_PET_PER_NIGHT;
  const isPetSupportedMappingType = isAirbnbMappingType || isVrboMappingType;
  const isPetScopeNotSupported =
    isPetRelatedScope && !isPetSupportedMappingType;

  const isPercentageOfTotalScope =
    scopeValue === FeeScopeEnum.PERCENTAGE_OF_TOTAL;
  const isPercentageOfTotalScopeNotSupported =
    isPercentageOfTotalScope && !isVrboMappingType;

  const isConditionExclusiveLongStay =
    taxConditionLengthOfStayType === FeeConditionEnum.EXCLUSIVE_LONG_STAY;

  const isConditionByNight = taxConditionType === FeeConditionEnum.BY_NIGHT;
  const isConditionByNightSupportedMappingType =
    !isBookingDotComMappingType && !isHvmbMappingType;
  const isConditionByNightNotSupported =
    isConditionByNight && !isConditionByNightSupportedMappingType;

  const isTaxConditionNotSupported =
    isTypeTax &&
    taxConditionType === BY_LENGTH_OF_STAY_SCOPE_OPTION &&
    !isAirbnbMappingType &&
    !isVrboMappingType &&
    !isHvmbMappingType;

  const isMappingTypeUnsupported =
    isPetScopeNotSupported ||
    isConditionExclusiveLongStay ||
    isTaxConditionNotSupported ||
    isRollInFeeIntoRent ||
    isPercentageOfTotalScopeNotSupported ||
    isConditionByNightNotSupported;

  const isRightTooltipVisible =
    !isMappingTypeUnsupported && !isChannelActiveAtProperty;

  const getMappingTypeOptions = () => {
    if (isMappingTypeUnsupported) {
      return [
        {
          value: 'UNSUPPORTED',
          label: t(
            'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.notSupported',
          ),
        },
      ];
    }
    return [
      ...(includeEmptySelectOption
        ? [
            {
              value: '',
              label: t(
                'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.pleaseSelect',
              ),
            },
          ]
        : []),
      ...options
        .map((value): { value: string; label: string } => ({
          value,
          label: t(
            normalizeKey(
              `pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.channelSpecific.${name}Options.${value}`,
            ),
          ),
        }))
        .sort((a, b) => {
          return a.label.localeCompare(b.label);
        }),
    ];
  };

  // Set null value when mapping type is unsupported
  useEffect(() => {
    if (isFetching) {
      return;
    }

    if (isMappingTypeUnsupported) {
      setValue(`${name}MappingType`, null);
    }
  }, [isMappingTypeUnsupported, isFetching, name, setValue]);

  const field = (
    <MappingTypeContainer
      className={classNames({
        'has-error': isMappingTypeUnsupported,
        'has-right-tooltip': isRightTooltipVisible,
      })}
    >
      <SelectField
        name={`${name}MappingType`}
        label={label}
        {...labelInputSizes}
        options={getMappingTypeOptions()}
        disabled={isMappingTypeUnsupported}
        onChangeHandler={onChangeHandler}
        rightTooltip={
          isRightTooltipVisible
            ? t(
                'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.channelNotAvailableProperty',
              )
            : undefined
        }
      />
      {icon}
    </MappingTypeContainer>
  );

  if (isMappingTypeUnsupported) {
    return (
      <Tooltip
        id={`tooltip-${name}`}
        text={t(
          'pageProperty.feesTaxesAndPolicies.propertyFeesAndTaxes.addModal.channelNotAvailableScope',
        )}
      >
        {field}
      </Tooltip>
    );
  }

  return field;
};

export default MappingTypeField;
